export interface MirrorWindsorReportRequest {
  provider: 'windsor';
  action: 'report_request';
  raw_text: string;
}
const fold = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').replace(/[łŁ]/gu, 'l')
  .toLowerCase().replace(/(?<=\p{L})['’](?=\p{L})/gu, ' ').replace(/\s+/gu, ' ').trim();
const reads = 'mostre|mostra|consulte|veja|leia|liste|verifique|show|check|read|list|toon|bekijk|lees|controleer|muestra|consulta|lee|lista|comprueba|montre|consulte|lis|liste|verifie|mostra|consulta|leggi|elenca|controlla|zeige|prufe|lies|liste|pokaz|sprawdz|przeczytaj|wyswietl';
const reports = 'relatorio|relatorios|report|reports|rapport|rapporten|informe|informes|rapports|rapporto|rapporti|bericht|berichte|raport|raporty';
const polite = /^(?:por favor|voce pode|voce poderia|pode|poderia|please|can you|could you|would you|alsjeblieft|kun je|kunt u|puedes|podrias|s il vous plait|peux tu|pouvez vous|per favore|puoi|potresti|bitte|kannst du|konnen sie|prosze|czy mozesz),? /u;
function core(raw: string) {
  let value = fold(raw);
  for (let index = 0; index < 3 && polite.test(value); index++) value = value.replace(polite, '');
  return value.replace(/[.!?]$/u, '').trim();
}
/** This grants a local selector only. No source, account, field or date is inferred. */
export function mirrorWindsorReportCurrentRequest(raw: string): MirrorWindsorReportRequest | null {
  if (typeof raw !== 'string' || !raw.trim() || raw.length > 1024 || raw !== raw.normalize('NFC')
    || raw !== raw.normalize('NFKC') || /[\p{Cc}\p{Cf}\p{Cs}"“”‘‚„‟«»\x60<>]|(?:^|\s)'|(?:^|[^\p{L}\p{M}])\p{M}/u.test(raw)) return null;
  let value = core(raw);
  const found = [...value.matchAll(/(?:^| )(windsor)(?= |$)/gu)];
  if (found.length !== 1) return null;
  const match = found[0], start = (match.index ?? 0) + (match[0].startsWith(' ') ? 1 : 0), end = start + 7;
  value = (value.slice(0, start).trimEnd().replace(/(?:^| )(?:no|na|do|da|em|in|on|from|op|bij|en|de|dans|sur|nel|sul|su|auf|im|bei|w|z)$/u, '')
    + ' ' + value.slice(end)).replace(/\s+/gu, ' ').trim();
  if (!new RegExp('^(?:' + reads + ') (?:(?:o|os|a|as|meu|minhas|my|the|de|het|mijn|el|los|la|las|mi|les|le|mon|mes|il|i|mio|die|das|mein|meine|moj|moje) )?(?:' + reports + ')$', 'u').test(value)) return null;
  return { provider: 'windsor', action: 'report_request', raw_text: raw };
}
export function mirrorWindsorReportTurnTargeted(raw: string): boolean {
  if (typeof raw !== 'string' || !raw.trim()) return false;
  if (mirrorWindsorReportCurrentRequest(raw)) return true;
  return ['', ' '].some(separator => {
    const normalized = raw.normalize('NFKC').replace(/[\p{Cf}\p{Cc}\p{Cs}]/gu, separator);
    const quoted = /^\s*(?:"([\s\S]*)"|“([\s\S]*)”)\s*[.!?]?\s*$/u.exec(normalized);
    const authority = (quoted ? quoted[1] ?? quoted[2] : normalized).replace(/"[^"]*"|“[^”]*”/gu, ' ');
    const value = core(authority.replace(/[\p{P}\p{S}]/gu, ' '));
    return /(?:^| )windsor(?: |$)/u.test(value)
      && new RegExp('(?:^| )(?:' + reports + ')(?: |$)', 'u').test(value);
  });
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const nativeUrl = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value), hostname = url.hostname.toLowerCase().replace(/\.$/u, '');
    return ['http:', 'https:'].includes(url.protocol) && (hostname === 'windsor.ai' || hostname.endsWith('.windsor.ai'));
  } catch { return false; }
};
/** An unsolicited raw Windsor/MCP call fences all siblings in the same model batch. */
export function isMirrorWindsorReportToolAttempt(block: unknown): boolean {
  if (!object(block) || block.type !== 'tool_use') return false;
  const name = typeof block.name === 'string' ? block.name : '';
  const tokens = name.replace(/([a-z\d])([A-Z])/gu, '$1_$2').toLowerCase().split(/[^a-z0-9]+/u);
  const rawName=tokens.join('_');
  if (tokens.includes('windsor') || /(?:^|_)(?:get_data|get_fields|get_options|get_current_user|get_connectors|execute_action|create_custom_field|create_destination_task|upload_files|contact_windsor|get_custom_fields|get_destination_tasks|get_destination_setup_info|get_destinations|list_actions|get_connector_authorization_url|get_connector_connect_info|get_windsor_login_url|get_subscription_url|report_sources|report_schema|windsor_report_request)$/u.test(rawName)) return true;
  const seen = new WeakSet<object>();
  const scoped = (value: unknown, depth: number): boolean => {
    if (!object(value) || depth > 6 || seen.has(value)) return false;
    seen.add(value);
    if (value.report_contract === 'mirror_windsor_report_v1' || value.read_contract === 'mirror_windsor_report_v1'
      || typeof value.capability === 'string' && /^windsor[.:/]/iu.test(value.capability)
      || Object.entries(value).some(([key, field]) => /^(?:url|endpoint|href|api_url|service_url|request_url)$/iu.test(key) && nativeUrl(field))
      || typeof value.raw_text === 'string' && mirrorWindsorReportTurnTargeted(value.raw_text)) return true;
    if (String(value.provider).toLowerCase() === 'windsor'
      && !(name === 'list_creator_sources' && value.action === undefined && value.report_contract === undefined && value.read_contract === undefined && Object.keys(value).length === 1)) return true;
    return Object.values(value).some(child => Array.isArray(child) ? child.some(item => scoped(item, depth + 1)) : scoped(child, depth + 1));
  };
  return scoped(block.input, 0);
}
